import { Button, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Stack, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { adminUpdatePharmacyStatus } from "../../../../api";
import ImageModal from "../../../../components/Modals/ImageModal";
import { colors } from "../../../../styles";
import { IPharmacy } from "../../../../types";
import { formatDateTimeKor } from "../../../../utils";
import { FieldRow, ImageThumb, ObjectStatus, StatusSelect, ValiditySelect } from "./shared";

interface IPharmacyDraft {
    bizRegNoIsValid: boolean;
    businessRegistrationCertImageIsValid: boolean;
    establishmentRegistrationCertImageIsValid: boolean;
    healthcareInstitutionIdIsValid: boolean;
    status: ObjectStatus;
    rejectedReason: string;
}

interface IPharmacyModalProps {
    isOpen: boolean;
    onClose: () => void;
    userId: string;
    pharmacy: IPharmacy;
}

const toDraft = (pharmacy: IPharmacy): IPharmacyDraft => ({
    bizRegNoIsValid: pharmacy.bizRegNoIsValid,
    businessRegistrationCertImageIsValid: pharmacy.businessRegistrationCertImageIsValid,
    establishmentRegistrationCertImageIsValid: pharmacy.establishmentRegistrationCertImageIsValid,
    healthcareInstitutionIdIsValid: pharmacy.healthcareInstitutionIdIsValid,
    status: pharmacy.status,
    rejectedReason: pharmacy.rejectedReason ?? "",
});

export default function PharmacyModal({ isOpen, onClose, userId, pharmacy }: IPharmacyModalProps) {
    const toast = useToast();
    const queryClient = useQueryClient();
    const [draft, setDraft] = useState<IPharmacyDraft>(() => toDraft(pharmacy));
    const { isOpen: isImageOpen, onOpen: onOpenImage, onClose: onCloseImage } = useDisclosure();
    const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) setDraft(toDraft(pharmacy));
    }, [isOpen, pharmacy]);

    const isDirty = useMemo(() => {
        const original = toDraft(pharmacy);
        return JSON.stringify(original) !== JSON.stringify(draft);
    }, [pharmacy, draft]);

    const allValid =
        draft.bizRegNoIsValid &&
        draft.businessRegistrationCertImageIsValid &&
        draft.establishmentRegistrationCertImageIsValid &&
        draft.healthcareInstitutionIdIsValid;

    const updateMutation = useMutation({
        mutationFn: adminUpdatePharmacyStatus,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["adminGetUsers"] });
            toast({ title: "약국 정보가 저장되었습니다.", status: "success", position: "top", duration: 2000, isClosable: true });
            onClose();
        },
        onError: () => {
            toast({ title: "약국 정보 저장에 실패했습니다.", status: "error", position: "top", duration: 2000, isClosable: true });
        },
    });

    const onSave = () => {
        updateMutation.mutate({
            userId,
            pharmacyId: pharmacy.id,
            status: draft.status,
            rejectedReason: draft.rejectedReason.trim() || null,
            bizRegNoIsValid: draft.bizRegNoIsValid,
            businessRegistrationCertImageIsValid: draft.businessRegistrationCertImageIsValid,
            establishmentRegistrationCertImageIsValid: draft.establishmentRegistrationCertImageIsValid,
            healthcareInstitutionIdIsValid: draft.healthcareInstitutionIdIsValid,
        });
    };

    const openImage = (url: string) => {
        setSelectedImageUrl(url);
        onOpenImage();
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <Text fontSize={20} fontWeight={700} color={colors.black}>약국 정보</Text>
                        <ModalCloseButton />
                    </ModalHeader>
                    <ModalBody>
                        <Stack spacing={1} width="100%">
                            <FieldRow label="이름">
                                <Text fontSize={14}>{pharmacy.name || "-"}</Text>
                            </FieldRow>
                            <FieldRow label="주소">
                                <Text fontSize={14}>{pharmacy.address || "-"}</Text>
                            </FieldRow>
                            <FieldRow label="사업자등록번호">
                                <Text fontSize={14}>{pharmacy.bizRegNo || "-"}</Text>
                            </FieldRow>
                            <FieldRow label="사업자등록번호 유효성">
                                <ValiditySelect
                                    value={draft.bizRegNoIsValid}
                                    onChange={(bizRegNoIsValid) => setDraft((prev) => ({ ...prev, bizRegNoIsValid }))}
                                />
                            </FieldRow>
                            <FieldRow label="요양기관 번호">
                                <Text fontSize={14}>{pharmacy.healthcareInstitutionId || "-"}</Text>
                            </FieldRow>
                            <FieldRow label="요양기관 번호 유효성">
                                <ValiditySelect
                                    value={draft.healthcareInstitutionIdIsValid}
                                    onChange={(healthcareInstitutionIdIsValid) => setDraft((prev) => ({ ...prev, healthcareInstitutionIdIsValid }))}
                                />
                            </FieldRow>
                            <FieldRow label="사업자 등록증">
                                <ImageThumb
                                    url={pharmacy.businessRegistrationCertImage}
                                    alt="businessRegistrationCertImage"
                                    onClick={openImage}
                                />
                            </FieldRow>
                            <FieldRow label="사업자 등록증 유효성">
                                <ValiditySelect
                                    value={draft.businessRegistrationCertImageIsValid}
                                    onChange={(businessRegistrationCertImageIsValid) => setDraft((prev) => ({ ...prev, businessRegistrationCertImageIsValid }))}
                                />
                            </FieldRow>
                            <FieldRow label="약국 개설 등록증">
                                <ImageThumb
                                    url={pharmacy.establishmentRegistrationCertImage}
                                    alt="establishmentRegistrationCertImage"
                                    onClick={openImage}
                                />
                            </FieldRow>
                            <FieldRow label="약국 개설 등록증 유효성">
                                <ValiditySelect
                                    value={draft.establishmentRegistrationCertImageIsValid}
                                    onChange={(establishmentRegistrationCertImageIsValid) => setDraft((prev) => ({ ...prev, establishmentRegistrationCertImageIsValid }))}
                                />
                            </FieldRow>
                            <FieldRow label="상태">
                                <StatusSelect
                                    value={draft.status}
                                    rejectDisabled={allValid}
                                    onChange={(status) => {
                                        if (allValid && status === "REJECTED") return;
                                        setDraft((prev) => ({ ...prev, status }));
                                    }}
                                />
                            </FieldRow>
                            <FieldRow label="거절 사유">
                                <Input
                                    size="sm"
                                    value={draft.rejectedReason}
                                    placeholder="거절 사유"
                                    onChange={(event) => setDraft((prev) => ({ ...prev, rejectedReason: event.target.value }))}
                                />
                            </FieldRow>
                            <FieldRow label="생성일">
                                <Text fontSize={14}>{formatDateTimeKor(String(pharmacy.createdAt))}</Text>
                            </FieldRow>
                            <FieldRow label="수정일">
                                <Text fontSize={14}>{formatDateTimeKor(String(pharmacy.updatedAt))}</Text>
                            </FieldRow>
                        </Stack>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            width="100%"
                            backgroundColor={colors.blue}
                            color={colors.white}
                            borderRadius={10}
                            isDisabled={!isDirty}
                            isLoading={updateMutation.isPending}
                            onClick={onSave}
                        >
                            저장
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            {selectedImageUrl && (
                <ImageModal isOpen={isImageOpen} onClose={onCloseImage} imageUrl={selectedImageUrl} />
            )}
        </>
    );
}
